/**
 * Passkey WebAuthn-PRF unlock path.
 *
 * Uses the WebAuthn PRF extension to derive a wrap key from a hardware
 * authenticator, then wraps/unwraps the session AEAD key stored in IndexedDB.
 *
 * Supports two operations:
 *  - enrollPasskey: registers a new credential and wraps the session key under
 *    the PRF output. Called after a successful passphrase unlock.
 *  - unlockWithPasskey: retrieves the stored credential, runs
 *    navigator.credentials.get with the PRF extension, unwraps the session key.
 *  - hasEnrolledPasskey: checks whether a wrapped entry exists for the userId.
 *
 * If the authenticator does not support PRF, enrollPasskey returns false and
 * the UI falls back to passphrase-only mode.
 *
 * Security note: the PRF output is used as a wrap key for the session AEAD
 * key. The session AEAD key itself is the Argon2id-derived key from the
 * passphrase unlock. This means a device-lost scenario still allows recovery
 * via the passphrase.
 */

import {
  startRegistration,
  startAuthentication,
  type RegistrationResponseJSON,
  type AuthenticationResponseJSON,
} from '@simplewebauthn/browser';
import { getWrappedKey, putWrappedKey, type WrappedKeyEntry } from './db.js';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function uint8ArrayToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.length; i++) {
    binary += String.fromCharCode(bytes[i] ?? 0);
  }
  return btoa(binary);
}

function base64ToUint8Array(b64: string): Uint8Array {
  const binary = atob(b64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

/**
 * Copies a Uint8Array into a fresh ArrayBuffer (not SharedArrayBuffer).
 * Required because SubtleCrypto rejects SharedArrayBuffer inputs.
 */
function toArrayBuffer(bytes: Uint8Array): ArrayBuffer {
  const ab = new ArrayBuffer(bytes.length);
  new Uint8Array(ab).set(bytes);
  return ab;
}

/**
 * Wraps a key with AES-256-GCM using a raw wrapping key and a random IV.
 * Returns { iv, ciphertext } as base64 strings.
 */
async function aeadWrapKey(
  keyToWrap: Uint8Array,
  wrappingKeyBytes: Uint8Array,
): Promise<{ iv: string; ciphertext: string }> {
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const wrappingKey = await crypto.subtle.importKey(
    'raw',
    toArrayBuffer(wrappingKeyBytes),
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt'],
  );
  const ciphertextBuffer = await crypto.subtle.encrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(iv) },
    wrappingKey,
    toArrayBuffer(keyToWrap),
  );
  return {
    iv: uint8ArrayToBase64(iv),
    ciphertext: uint8ArrayToBase64(new Uint8Array(ciphertextBuffer)),
  };
}

/**
 * Unwraps a key that was wrapped with aeadWrapKey().
 * Returns the unwrapped key bytes, or throws on auth failure.
 */
async function aeadUnwrapKey(
  ivB64: string,
  ciphertextB64: string,
  wrappingKeyBytes: Uint8Array,
): Promise<Uint8Array> {
  const iv = base64ToUint8Array(ivB64);
  const ciphertext = base64ToUint8Array(ciphertextB64);
  const wrappingKey = await crypto.subtle.importKey(
    'raw',
    toArrayBuffer(wrappingKeyBytes),
    { name: 'AES-GCM', length: 256 },
    false,
    ['decrypt'],
  );
  const plaintextBuffer = await crypto.subtle.decrypt(
    { name: 'AES-GCM', iv: toArrayBuffer(iv) },
    wrappingKey,
    toArrayBuffer(ciphertext),
  );
  return new Uint8Array(plaintextBuffer);
}

// ---------------------------------------------------------------------------
// PRF salt — a fixed per-origin salt for PRF evaluation.
// Using a deterministic value is intentional: we need the same PRF output
// on registration and authentication. The authenticator's hardware secret
// provides the entropy; this salt is just a domain-separation label.
// ---------------------------------------------------------------------------

const PRF_SALT = new TextEncoder().encode('pf-passkey-prf-salt-v1');

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Returns true if the userId has an enrolled passkey in IndexedDB.
 */
export async function hasEnrolledPasskey(userId: string): Promise<boolean> {
  const entry = await getWrappedKey(userId);
  return entry !== null;
}

/**
 * Enrols a new passkey for the userId.
 *
 * 1. Calls navigator.credentials.create with the PRF extension.
 * 2. Reads the PRF output. If PRF is not supported, returns false.
 * 3. Wraps the session AEAD key under the PRF output.
 * 4. Stores { credentialId, salt1, iv, ciphertext } in IndexedDB.
 *
 * Returns true on success, false if PRF is not supported by the device.
 */
export async function enrollPasskey(
  userId: string,
  sessionKey: Uint8Array,
): Promise<boolean> {
  const challenge = crypto.getRandomValues(new Uint8Array(32));
  const userIdBytes = new TextEncoder().encode(userId);

  let regResponse: RegistrationResponseJSON;
  try {
    regResponse = await startRegistration({
      optionsJSON: {
        challenge: uint8ArrayToBase64(challenge),
        rp: {
          name: 'PersonalFinance',
          id: window.location.hostname,
        },
        user: {
          id: uint8ArrayToBase64(userIdBytes),
          name: userId,
          displayName: userId,
        },
        pubKeyCredParams: [
          { alg: -7, type: 'public-key' },   // ES256
          { alg: -257, type: 'public-key' }, // RS256
        ],
        authenticatorSelection: {
          residentKey: 'preferred',
          userVerification: 'required',
        },
        extensions: {
          // @ts-expect-error — PRF extension not in all WebAuthn TS types yet
          prf: { eval: { first: uint8ArrayToBase64(PRF_SALT) } },
        },
      },
    });
  } catch {
    // User cancelled or authenticator not available
    return false;
  }

  // Extract PRF output
  // @ts-expect-error — PRF extension results not in all WebAuthn TS types
  const prfResults = regResponse.clientExtensionResults?.prf?.results;
  const prfFirst: string | undefined = prfResults?.first;

  if (!prfFirst) {
    // Device doesn't support PRF — fall back to passphrase
    return false;
  }

  const prfBytes = base64ToUint8Array(prfFirst);
  const { iv, ciphertext } = await aeadWrapKey(sessionKey, prfBytes);

  const entry: WrappedKeyEntry = {
    credentialId: regResponse.id,
    salt1: uint8ArrayToBase64(PRF_SALT),
    iv,
    ciphertext,
  };
  await putWrappedKey(userId, entry);
  return true;
}

/**
 * Attempts to unlock using a stored passkey for the given userId.
 *
 * 1. Looks up the wrapped key entry in IndexedDB.
 * 2. Calls navigator.credentials.get with the PRF extension.
 * 3. Derives the wrap key from the PRF output.
 * 4. Unwraps and returns the session AEAD key.
 *
 * Returns null if:
 *  - No enrolled passkey exists for the userId.
 *  - The user cancels the prompt.
 *  - The authenticator does not return a PRF result.
 *  - Unwrapping fails.
 */
export async function unlockWithPasskey(
  userId: string,
): Promise<{ key: Uint8Array } | null> {
  const entry = await getWrappedKey(userId);
  if (!entry) return null;

  const credId = base64ToUint8Array(entry.credentialId);

  let authResponse: AuthenticationResponseJSON;
  try {
    authResponse = await startAuthentication({
      optionsJSON: {
        challenge: uint8ArrayToBase64(crypto.getRandomValues(new Uint8Array(32))),
        rpId: window.location.hostname,
        allowCredentials: [
          { id: uint8ArrayToBase64(credId), type: 'public-key' },
        ],
        userVerification: 'required',
        extensions: {
          // @ts-expect-error — PRF extension not in all WebAuthn TS types yet
          prf: { eval: { first: entry.salt1 } },
        },
      },
    });
  } catch {
    // User cancelled or device error
    return null;
  }

  // @ts-expect-error — PRF extension results not in all WebAuthn TS types
  const prfResults = authResponse.clientExtensionResults?.prf?.results;
  const prfFirst: string | undefined = prfResults?.first;

  if (!prfFirst) {
    // Authenticator did not return PRF output
    return null;
  }

  const prfBytes = base64ToUint8Array(prfFirst);

  try {
    const key = await aeadUnwrapKey(entry.iv, entry.ciphertext, prfBytes);
    return { key };
  } catch {
    // Wrong credential / PRF mismatch
    return null;
  }
}
